import React, { useCallback, useRef } from 'react';

import styled from 'styled-components';

import { SaveButton, SaveButtonStatus } from '../Button/SaveButton';

import { PictureUploaderProps } from './PictureUploader.decl';

import { Box, Button, Flex, Typography, useTheme } from '@aircall/tractor';
import { useSaveStatusHook } from '@hooks/useSaveStatusHook/useSaveStatusHook';
import { useTranslation } from 'react-i18next';

const HIDE_BUTTON_TIMEOUT = 3000;

function ErrorTab({
  error,
  onRetry,
  retryLabel,
}: {
  error: string;
  onRetry: () => void;
  retryLabel: string;
}) {
  return (
    <Flex
      backgroundColor='red.lighter'
      justifyContent='space-between'
      alignItems='center'
      height={48}
      px={16}
    >
      <Typography variant='caption2'>{error}</Typography>
      <Button variant='black' mode='outline' onClick={onRetry} data-test='retry-button'>
        {retryLabel}
      </Button>
    </Flex>
  );
}

const Input = styled.input`
  display: none;
`;

function getSaveButtonStatus(loading: boolean, saved: boolean) {
  if (loading) {
    return SaveButtonStatus.SAVING;
  }
  if (saved) {
    return SaveButtonStatus.SAVED;
  }
  return SaveButtonStatus.UNSAVED;
}

export function PictureUploader({
  hasPictureUrl,
  avatar,
  deleteState,
  updateState,
  onDelete,
  onUpdate,
}: PictureUploaderProps) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const inputEl = useRef<HTMLInputElement>(null);
  const { loading: isDeleting, error: deleteError, saved: isDeletionSaved } = deleteState;
  const { loading: isUpdating, error: updateError, saved: isUpdateSaved } = updateState;

  const isDeletedLabelVisible = useSaveStatusHook(isDeletionSaved, HIDE_BUTTON_TIMEOUT);
  const isUpdatedLabelVisible = useSaveStatusHook(isUpdateSaved, HIDE_BUTTON_TIMEOUT);

  const openFileBrowser = useCallback(() => {
    /* istanbul ignore else */
    if (inputEl) {
      inputEl.current?.click();
    }
  }, [inputEl]);

  const onSelectedFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      await onUpdate(event);
      // clear previously selected file so `onSelectedFileChange` can be triggered if the user select the same picture for the second time
      inputEl.current!.value = '';
    },
    [onUpdate]
  );

  const renderActionTab = () => {
    if (deleteError || updateError) {
      // show error message and retry button
      return (
        <ErrorTab
          error={t((deleteError || updateError) as string)}
          onRetry={deleteError ? onDelete : openFileBrowser}
          retryLabel={t('picture_uploader.retry')}
        />
      );
    }
    return (
      // show upload button and/or delete button
      <Flex borderTop={`1px solid ${colors.grey.base}`}>
        {(isDeletedLabelVisible || hasPictureUrl) && (
          <Box
            borderRight={`1px solid ${colors.grey.base}`}
            flexGrow={1}
            flexBasis={0}
            py={10}
            data-test='piture-uploader-delete-box'
          >
            <SaveButton
              data-test='delete-button'
              mode='link'
              size='xSmall'
              type='button'
              block
              onClick={onDelete}
              disabled={isDeleting}
              status={getSaveButtonStatus(isDeleting, isDeletedLabelVisible)}
              content={t('picture_uploader.delete')}
              savedContent={t('picture_uploader.deleted')}
            />
          </Box>
        )}
        <Box flexGrow={1} flexBasis={0} py={10}>
          <SaveButton
            data-test='update-button'
            mode='link'
            size='xSmall'
            type='button'
            block
            status={getSaveButtonStatus(isUpdating, isUpdatedLabelVisible)}
            disabled={isUpdating}
            onClick={openFileBrowser}
            content={t('picture_uploader.upload')}
            savedContent={t('picture_uploader.uploaded')}
          />
        </Box>
      </Flex>
    );
  };

  return (
    <Box
      borderRadius={8}
      border={`1px solid ${colors.grey.base}`}
      backgroundColor={colors.base.white}
      overflow='hidden'
    >
      <Flex py='l' flexDirection='column' alignItems='center'>
        {avatar}
      </Flex>
      <Input
        type='file'
        accept='image/png,image/jpeg'
        onChange={onSelectedFileChange}
        ref={inputEl}
        data-test='file-input'
      />
      {renderActionTab()}
    </Box>
  );
}
